import { useMemo } from 'react';
import { readUser } from '@app/services/localStorage.service';

interface PermissionLike {
  controller_name?: string;
  action_name?: string;
}

export interface RoleLike {
  name?: string;
  permissions?: PermissionLike[] | PermissionLike | null;
}

export interface PermissionChecker {
  can: (controller: string, action?: string) => boolean;
  /**
   * Chỉ admin hoặc quyền được cấp đích danh — KHÔNG mặc định cho manager/accountant.
   * Dùng cho policy backend không nằm trong COMMON_ACTIONS (phiên đăng nhập, audit log).
   */
  hasGrant: (controller: string, action?: string) => boolean;
  isSuperUser: boolean;
}

const SUPER_ROLES = ['admin', 'manager', 'accountant'];
// index/show cũng đạt khi có view_own — khớp ApplicationPolicy#index?/show? ở backend.
const VIEW_OWN_ACTIONS = new Set(['index', 'show']);

const singularize = (name: string): string => {
  if (/(us|ss)$/.test(name)) return name;
  if (name.endsWith('ies')) return `${name.slice(0, -3)}y`;
  if (name.endsWith('ses')) return name.slice(0, -2);
  return name.replace(/s$/, '');
};

const pluralize = (name: string): string => {
  if (name.endsWith('y')) return `${name.slice(0, -1)}ies`;
  return name.endsWith('s') ? `${name}es` : `${name}s`;
};

// Backend check_permission? chấp nhận cả tên controller số ít lẫn số nhiều.
const controllerVariants = (controller: string): string[] => {
  const singular = singularize(controller);
  return Array.from(new Set([controller, singular, pluralize(singular)]));
};

const toArray = (permissions: RoleLike['permissions']): PermissionLike[] => {
  if (Array.isArray(permissions)) return permissions;
  return permissions ? [permissions] : [];
};

export const buildPermissionChecker = (roles?: RoleLike[] | null): PermissionChecker => {
  const list = roles ?? [];
  const isSuperUser = list.some((role) => SUPER_ROLES.includes(String(role.name ?? '').toLowerCase()));
  const permSet = new Set(
    list.flatMap((role) => toArray(role.permissions).map((p) => `${p.controller_name}:${p.action_name}`)),
  );

  const isAdmin = list.some((role) => String(role.name ?? '').toLowerCase() === 'admin');
  const granted = (controller: string, actions: string[]): boolean =>
    controllerVariants(controller).some((name) => actions.some((a) => permSet.has(`${name}:${a}`)));

  const can = (controller: string, action = 'index'): boolean => {
    if (isSuperUser) return true;
    return granted(controller, VIEW_OWN_ACTIONS.has(action) ? [action, 'view_own'] : [action]);
  };

  const hasGrant = (controller: string, action = 'index'): boolean => isAdmin || granted(controller, [action]);

  return { can, hasGrant, isSuperUser };
};

export const usePermissions = (): PermissionChecker => {
  // readUser() parse lại localStorage mỗi render → memo theo nội dung roles, không theo reference.
  const rolesKey = JSON.stringify(readUser()?.roles ?? []);
  return useMemo(() => buildPermissionChecker(JSON.parse(rolesKey)), [rolesKey]);
};
